// D-17 — the ONE typed error normalizer for the Data surfaces. Maps a caught error (already classified
// into the denialErrors typed vocabulary by classifyFetchError) into a display model the UI can render
// uniformly: category + stable code + user-facing copy + retryability + field errors. Classification is
// by ERROR TYPE (instanceof) and, for 409/422, by the backend's stable `code` — NEVER by sniffing the
// message text. Only categories with real backend behavior are represented (audited against the Data
// endpoints, 2026-07-20). A cancellation (AbortController) is NOT a failure — callers check
// `isCancellation` and stay silent.
import {
  ConflictError,
  ForbiddenError,
  PayloadTooLargeError,
  RateLimitedError,
  ScopeSealedOrMissingError,
  ServerError,
  SessionExpiredError,
  ValidationError,
  UnsupportedMediaTypeError,
} from "./denialErrors";

export type ErrorCategory =
  | "AUTHENTICATION_REQUIRED"
  | "NOT_AUTHORIZED"
  | "SEALED_OR_NOT_FOUND"
  | "CONFLICT"
  | "LEGAL_HOLD"
  | "LINEAGE_CONFLICT"
  | "RETRY_NOT_ALLOWED"
  | "VALIDATION_ERROR"
  | "PAYLOAD_TOO_LARGE"
  | "UNSUPPORTED_MEDIA_TYPE"
  | "RATE_LIMITED"
  | "SERVICE_UNAVAILABLE"
  | "OFFLINE"
  | "CANCELLED"
  | "UNKNOWN";

export interface ErrorDisplay {
  category: ErrorCategory;
  /** the backend's stable code when present (409/422/403), e.g. SOURCE_HAS_DERIVED_MEANING. */
  code?: string;
  /** short label for a heading/badge. */
  title: string;
  /** one-line, user-safe explanation — never a raw stack or backend traceback. */
  detail: string;
  /** does an explicit "Retry" of the SAME request make sense? (transient failures only). */
  retryable: boolean;
  /** per-field validation messages when the backend supplied them (422). */
  fieldErrors?: Record<string, string>;
  /** true only for an aborted/cancelled request — the caller must NOT surface it as an error. */
  isCancellation: boolean;
}

function isAbort(e: unknown): boolean {
  // AbortController rejects with a DOMException named "AbortError"; some polyfills use a plain object.
  return typeof e === "object" && e !== null && (e as { name?: string }).name === "AbortError";
}

// 409 conflict codes → the specific category + copy. Unlisted codes fall to a generic CONFLICT.
const CONFLICT_COPY: Record<string, { category: ErrorCategory; title: string; detail: string }> = {
  SOURCE_HAS_DERIVED_MEANING: {
    category: "LINEAGE_CONFLICT", title: "Has derived meaning",
    detail: "This source can’t be deleted because governed meaning was derived from it.",
  },
  SOURCE_UNDER_LEGAL_HOLD: {
    category: "LEGAL_HOLD", title: "Under legal hold",
    detail: "This source is under a legal hold and can’t be changed.",
  },
  DUPLICATE_SOURCE: {
    category: "CONFLICT", title: "Already exists",
    detail: "A source with identical content already exists at this scope.",
  },
  NOT_QUARANTINED: {
    category: "CONFLICT", title: "Not quarantined",
    detail: "This source isn’t quarantined, so there’s nothing to release.",
  },
  RETRY_NOT_ALLOWED: {
    category: "RETRY_NOT_ALLOWED", title: "Retry not allowed",
    detail: "Processing can’t be retried in its current state.",
  },
  NO_PROCESSING_STATE: {
    category: "CONFLICT", title: "No processing to retry",
    detail: "This source has no processing run to retry yet.",
  },
  // MB4 governed-result conflicts (§6/§9) — user-safe copy, never the raw backend message.
  RESULT_APPROVED_IMMUTABLE: {
    category: "CONFLICT", title: "Already approved",
    detail: "This result is approved and can’t be changed - supersede it to make a new version.",
  },
  ILLEGAL_REVIEW_TRANSITION: {
    category: "CONFLICT", title: "Invalid review step",
    detail: "That review action isn’t valid from the result’s current state.",
  },
  SELF_APPROVAL_FORBIDDEN: {
    category: "CONFLICT", title: "Can’t self-approve",
    detail: "You can’t approve a result you produced.",
  },
  SUPERSEDE_TARGET_NOT_FOUND: {
    category: "CONFLICT", title: "Nothing to supersede",
    detail: "The result to supersede no longer exists.",
  },
  CROSS_SCOPE_SUPERSEDE: {
    category: "CONFLICT", title: "Different scope",
    detail: "A result can’t supersede one in a different scope.",
  },
};

/** Normalize any caught error into a typed, user-safe display model. `e` is expected to already be a
 * denialErrors-classified error (from a scoped fetch); an unrecognized value falls to UNKNOWN. */
export function describeError(e: unknown): ErrorDisplay {
  if (isAbort(e)) {
    return { category: "CANCELLED", title: "Cancelled", detail: "The request was cancelled.", retryable: false, isCancellation: true };
  }
  if (e instanceof SessionExpiredError) {
    return { category: "AUTHENTICATION_REQUIRED", title: "Sign in again", detail: "Your session expired - sign in to continue.", retryable: false, isCancellation: false };
  }
  if (e instanceof ForbiddenError) {
    return { category: "NOT_AUTHORIZED", code: e.code, title: "Not allowed", detail: e.message || "You don’t have access to do that here.", retryable: false, isCancellation: false };
  }
  if (e instanceof ScopeSealedOrMissingError) {
    return { category: "SEALED_OR_NOT_FOUND", title: "Not available", detail: "This item isn’t available at your current scope.", retryable: false, isCancellation: false };
  }
  if (e instanceof ConflictError) {
    // known code → safe copy; unknown code → a GENERIC line (never echo the raw backend message).
    const c = (e.code && CONFLICT_COPY[e.code]) || { category: "CONFLICT" as ErrorCategory, title: "Conflict", detail: "That action conflicts with the current state." };
    return { category: c.category, code: e.code, title: c.title, detail: c.detail, retryable: false, isCancellation: false };
  }
  if (e instanceof ValidationError) {
    return { category: "VALIDATION_ERROR", code: e.code, title: "Check your input", detail: e.message || "Some values weren’t accepted.", retryable: false, fieldErrors: e.fields, isCancellation: false };
  }
  if (e instanceof PayloadTooLargeError) {
    return { category: "PAYLOAD_TOO_LARGE", title: "Attachment too large", detail: "Choose a file that is 25 MB or smaller and try again.", retryable: false, isCancellation: false };
  }
  if (e instanceof UnsupportedMediaTypeError) {
    return { category: "UNSUPPORTED_MEDIA_TYPE", title: "File type not supported", detail: "Attach a PNG, JPG, WebP, PDF, or TXT file and try again.", retryable: false, isCancellation: false };
  }
  if (e instanceof RateLimitedError) {
    const wait = e.retryAfterSeconds ? ` Wait ${e.retryAfterSeconds} seconds before retrying.` : " Wait a moment before retrying.";
    return { category: "RATE_LIMITED", title: "Too many requests", detail: `Anand has received several requests.${wait}`, retryable: true, isCancellation: false };
  }
  if (e instanceof ServerError) {
    // A 5xx is retryable BY DEFAULT, but the server may declare one permanent — an upstream that
    // refused us for a configuration reason will refuse identically next time. Honour that verdict
    // rather than recomputing it here: telling someone to retry a refusal is advice that cannot
    // work. `e.message` stays unrendered on purpose — a 5xx body can carry upstream internals, and
    // `detail` is contracted user-safe (see the type above and zarvisApi.test.ts).
    return e.retryable
      ? { category: "SERVICE_UNAVAILABLE", title: "Service problem", detail: "The server had a problem. Please try again.", retryable: true, isCancellation: false }
      : { category: "SERVICE_UNAVAILABLE", code: e.code, title: "Service problem", detail: "The server refused this request. Retrying will not help until it is resolved.", retryable: false, isCancellation: false };
  }
  if (e instanceof TypeError) {
    // fetch rejects with a TypeError on a network failure before any Response exists.
    return { category: "OFFLINE", title: "You are offline", detail: "Could not reach the server. Check your connection and retry.", retryable: true, isCancellation: false };
  }
  // Unknown: surface honestly but NEVER leak a raw stack/backend traceback — a generic, retryable copy.
  return { category: "UNKNOWN", title: "Something went wrong", detail: "An unexpected error occurred. Please try again.", retryable: true, isCancellation: false };
}
